// Evergreen date helpers for the sales demo.
//
// A demo that shows "today" as a date months in the past reads as broken to a
// prospect. So every fixture timestamp is computed RELATIVE to the moment the
// console loads — "today" is always actually today, the upcoming event is always
// next week, the last payout was always a few days ago. Nothing to re-seed.
//
// Runs in the browser, so `Date` is fully available here (unlike workflow
// scripts). All helpers are pure functions of the current wall clock.

const MS_PER_DAY = 24 * 60 * 60 * 1000
const MS_PER_HOUR = 60 * 60 * 1000
const MS_PER_MIN = 60 * 1000

/** Now, as a Date. Centralized so every fixture shares one clock read per build. */
export function now(): Date {
  return new Date()
}

/** ISO-8601 timestamp `n` days from now (negative = past). */
export function isoDaysFromNow(days: number): string {
  return new Date(Date.now() + days * MS_PER_DAY).toISOString()
}

/** ISO-8601 timestamp `n` hours from now (negative = past). */
export function isoHoursFromNow(hours: number): string {
  return new Date(Date.now() + hours * MS_PER_HOUR).toISOString()
}

/** ISO-8601 timestamp `n` minutes from now (negative = past). */
export function isoMinutesFromNow(mins: number): string {
  return new Date(Date.now() + mins * MS_PER_MIN).toISOString()
}

/** Date-only string (YYYY-MM-DD) `n` days from now, in local time. */
export function dateOnlyDaysFromNow(days: number): string {
  const d = new Date(Date.now() + days * MS_PER_DAY)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Unix epoch SECONDS `n` days from now — Stripe-style timestamps. */
export function unixDaysFromNow(days: number): number {
  return Math.floor((Date.now() + days * MS_PER_DAY) / 1000)
}

/** ISO timestamp pinned to a given hour today (local), for "paid at 2pm" rows. */
export function isoTodayAt(hour: number, minute = 0): string {
  const d = new Date()
  d.setHours(hour, minute, 0, 0)
  return d.toISOString()
}

/** ISO timestamp pinned to a given hour `n` days ago (local). */
export function isoDaysAgoAt(days: number, hour: number, minute = 0): string {
  const d = new Date(Date.now() - days * MS_PER_DAY)
  d.setHours(hour, minute, 0, 0)
  return d.toISOString()
}
